import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePreset } from '../../../../shared/interfaces/analytics.interface';
import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns';
import { FILTERABLE_STATUSES, formatRange, PAYMENT_METHOD_LABELS, STATUS_META } from '../../utils/report-format';
import { ReportFilterLabel, ReportFilterState } from '../../utils/report-context';

interface Option {
  id: string;
  name: string;
}

const MAX_RANGE_DAYS = 366;

@Component({
  selector: 'app-analytics-filters',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './analytics-filters.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalyticsFilters implements OnInit {
  @Input() professionals: Option[] = [];
  @Input() services: Option[] = [];
  /** Los profesionales solo ven sus propios datos: el backend ya los limita */
  @Input() canFilterProfessional = true;
  @Output() filtersChanged = new EventEmitter<ReportFilterState>();

  showFilters = signal(false);
  activePreset = signal<DatePreset>('30d');
  rangeError = signal('');

  selectedProfessionalId = '';
  selectedServiceId = '';
  selectedStatusCode = '';
  selectedPaymentMethod = '';
  customStartDate = '';
  customEndDate = '';

  readonly statusOptions = FILTERABLE_STATUSES.map((value) => ({ value, label: STATUS_META[value].label }));
  readonly paymentMethodOptions = Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }));

  readonly presets: { value: Exclude<DatePreset, 'custom'>; label: string }[] = [
    { value: 'today', label: 'Hoy' },
    { value: '7d', label: '7 días' },
    { value: '30d', label: '30 días' },
    { value: 'thisMonth', label: 'Este mes' },
    { value: 'lastMonth', label: 'Mes anterior' },
    { value: 'thisYear', label: 'Este año' },
  ];

  ngOnInit(): void {
    this.syncCustomInputs();
    this.emitFilters();
  }

  get activeFilterCount(): number {
    return [this.selectedProfessionalId, this.selectedServiceId, this.selectedStatusCode, this.selectedPaymentMethod]
      .filter(Boolean).length;
  }

  selectPreset(preset: Exclude<DatePreset, 'custom'>): void {
    this.activePreset.set(preset);
    this.rangeError.set('');
    this.syncCustomInputs();
    this.emitFilters();
  }

  applyCustomRange(): void {
    const start = parseISO(this.customStartDate);
    const end = parseISO(this.customEndDate);
    if (!isValid(start) || !isValid(end)) return;

    if (start > end) {
      this.rangeError.set('La fecha inicial debe ser anterior a la final.');
      return;
    }
    if (differenceInCalendarDays(end, start) + 1 > MAX_RANGE_DAYS) {
      this.rangeError.set('Elige un rango de máximo un año.');
      return;
    }
    this.rangeError.set('');
    this.activePreset.set('custom');
    this.emitFilters();
  }

  onFilterChange(): void {
    this.emitFilters();
  }

  removeFilter(key: ReportFilterLabel['key']): void {
    if (key === 'professional') this.selectedProfessionalId = '';
    if (key === 'service') this.selectedServiceId = '';
    if (key === 'status') this.selectedStatusCode = '';
    if (key === 'paymentMethod') this.selectedPaymentMethod = '';
    this.emitFilters();
  }

  clearFilters(): void {
    this.selectedProfessionalId = '';
    this.selectedServiceId = '';
    this.selectedStatusCode = '';
    this.selectedPaymentMethod = '';
    this.emitFilters();
  }

  toggleFilters(): void {
    this.showFilters.update((v) => !v);
  }

  /** Etiquetas visibles de los filtros activos (también van en el PDF y el Excel) */
  activeFilters(): ReportFilterLabel[] {
    const labels: ReportFilterLabel[] = [];
    if (this.selectedProfessionalId) {
      const name = this.professionals.find((p) => p.id === this.selectedProfessionalId)?.name ?? 'Seleccionado';
      labels.push({ key: 'professional', label: 'Profesional', value: name });
    }
    if (this.selectedServiceId) {
      const name = this.services.find((s) => s.id === this.selectedServiceId)?.name ?? 'Seleccionado';
      labels.push({ key: 'service', label: 'Servicio', value: name });
    }
    if (this.selectedStatusCode) {
      labels.push({ key: 'status', label: 'Estado', value: STATUS_META[this.selectedStatusCode]?.label ?? this.selectedStatusCode });
    }
    if (this.selectedPaymentMethod) {
      labels.push({ key: 'paymentMethod', label: 'Método de pago', value: PAYMENT_METHOD_LABELS[this.selectedPaymentMethod] ?? this.selectedPaymentMethod });
    }
    return labels;
  }

  private syncCustomInputs(): void {
    const { startDate, endDate } = this.resolveDateRange(this.activePreset());
    this.customStartDate = format(startDate, 'yyyy-MM-dd');
    this.customEndDate = format(endDate, 'yyyy-MM-dd');
  }

  private emitFilters(): void {
    const { startDate, endDate } = this.resolveDateRange(this.activePreset());

    const request: ReportFilterState['request'] = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
    if (this.selectedProfessionalId) request.professionalId = this.selectedProfessionalId;
    if (this.selectedServiceId) request.serviceId = this.selectedServiceId;
    if (this.selectedStatusCode) request.statusCode = this.selectedStatusCode;
    if (this.selectedPaymentMethod) request.paymentMethod = this.selectedPaymentMethod;

    this.filtersChanged.emit({
      request,
      rangeStart: startDate,
      rangeEnd: endDate,
      rangeLabel: formatRange(startDate, endDate),
      filters: this.activeFilters(),
    });
  }

  private resolveDateRange(preset: DatePreset): { startDate: Date; endDate: Date } {
    const now = new Date();
    switch (preset) {
      case 'today':
        return { startDate: startOfDay(now), endDate: endOfDay(now) };
      case '7d':
        return { startDate: startOfDay(subDays(now, 6)), endDate: endOfDay(now) };
      case '30d':
        return { startDate: startOfDay(subDays(now, 29)), endDate: endOfDay(now) };
      case 'thisMonth':
        return { startDate: startOfMonth(now), endDate: endOfDay(now) };
      case 'lastMonth': {
        const lastMonth = subMonths(now, 1);
        return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
      }
      case 'thisYear':
        return { startDate: startOfYear(now), endDate: endOfDay(now) };
      case 'custom': {
        // parseISO interpreta 'yyyy-MM-dd' en hora local (new Date() lo haría en UTC y correría un día)
        const start = parseISO(this.customStartDate);
        const end = parseISO(this.customEndDate);
        return {
          startDate: isValid(start) ? startOfDay(start) : startOfDay(subDays(now, 29)),
          endDate: isValid(end) ? endOfDay(end) : endOfDay(now),
        };
      }
    }
  }
}
